import { useEffect } from 'react';

interface SizeGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SizeGuideModal({ isOpen, onClose }: SizeGuideModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-black/10 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl tracking-[0.2em]">SIZE GUIDE</h2>
          <button 
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-neutral-50 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6 md:p-8">
          {/* How to Measure */}
          <div className="mb-8">
            <h3 className="text-sm tracking-[0.2em] mb-4">HOW TO MEASURE</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-sm mb-2"><strong>CHEST:</strong></p>
                <p className="text-sm text-neutral-600">Measure around the fullest part of your chest, keeping the tape horizontal.</p>
              </div>
              <div>
                <p className="text-sm mb-2"><strong>LENGTH:</strong></p>
                <p className="text-sm text-neutral-600">Measure from the highest point of the shoulder to the bottom hem.</p>
              </div>
              <div>
                <p className="text-sm mb-2"><strong>SHOULDER:</strong></p>
                <p className="text-sm text-neutral-600">Measure from shoulder point to shoulder point across the back.</p>
              </div>
              <div>
                <p className="text-sm mb-2"><strong>SLEEVE:</strong></p>
                <p className="text-sm text-neutral-600">Measure from the shoulder seam to the end of the cuff.</p>
              </div>
            </div>
          </div>

          {/* T-Shirts Size Chart */}
          <div className="mb-8">
            <h3 className="text-sm tracking-[0.2em] mb-4">T-SHIRTS</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-black/10">
                <thead>
                  <tr className="bg-neutral-50">
                    <th className="border border-black/10 px-4 py-3 text-left tracking-[0.1em]">SIZE</th>
                    <th className="border border-black/10 px-4 py-3 text-left">CHEST (inches)</th>
                    <th className="border border-black/10 px-4 py-3 text-left">LENGTH (inches)</th>
                    <th className="border border-black/10 px-4 py-3 text-left">SHOULDER (inches)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">S</td>
                    <td className="border border-black/10 px-4 py-3">18</td>
                    <td className="border border-black/10 px-4 py-3">27</td>
                    <td className="border border-black/10 px-4 py-3">17</td>
                  </tr>
                  <tr className="bg-neutral-50/50">
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">M</td>
                    <td className="border border-black/10 px-4 py-3">20</td>
                    <td className="border border-black/10 px-4 py-3">28</td>
                    <td className="border border-black/10 px-4 py-3">18</td>
                  </tr>
                  <tr>
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">L</td>
                    <td className="border border-black/10 px-4 py-3">22</td>
                    <td className="border border-black/10 px-4 py-3">29</td>
                    <td className="border border-black/10 px-4 py-3">19</td>
                  </tr>
                  <tr className="bg-neutral-50/50">
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">XL</td>
                    <td className="border border-black/10 px-4 py-3">24</td>
                    <td className="border border-black/10 px-4 py-3">30</td>
                    <td className="border border-black/10 px-4 py-3">20</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Hoodies Size Chart */}
          <div className="mb-8">
            <h3 className="text-sm tracking-[0.2em] mb-4">HOODIES</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-black/10">
                <thead>
                  <tr className="bg-neutral-50">
                    <th className="border border-black/10 px-4 py-3 text-left tracking-[0.1em]">SIZE</th>
                    <th className="border border-black/10 px-4 py-3 text-left">CHEST (inches)</th>
                    <th className="border border-black/10 px-4 py-3 text-left">LENGTH (inches)</th>
                    <th className="border border-black/10 px-4 py-3 text-left">SHOULDER (inches)</th>
                    <th className="border border-black/10 px-4 py-3 text-left">SLEEVE (inches)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">S</td>
                    <td className="border border-black/10 px-4 py-3">20</td>
                    <td className="border border-black/10 px-4 py-3">26</td>
                    <td className="border border-black/10 px-4 py-3">18</td>
                    <td className="border border-black/10 px-4 py-3">33</td>
                  </tr>
                  <tr className="bg-neutral-50/50">
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">M</td>
                    <td className="border border-black/10 px-4 py-3">22</td>
                    <td className="border border-black/10 px-4 py-3">27</td>
                    <td className="border border-black/10 px-4 py-3">19</td>
                    <td className="border border-black/10 px-4 py-3">34</td>
                  </tr>
                  <tr>
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">L</td>
                    <td className="border border-black/10 px-4 py-3">24</td>
                    <td className="border border-black/10 px-4 py-3">28</td>
                    <td className="border border-black/10 px-4 py-3">20</td>
                    <td className="border border-black/10 px-4 py-3">35</td>
                  </tr>
                  <tr className="bg-neutral-50/50">
                    <td className="border border-black/10 px-4 py-3 tracking-[0.1em]">XL</td>
                    <td className="border border-black/10 px-4 py-3">26</td>
                    <td className="border border-black/10 px-4 py-3">29</td>
                    <td className="border border-black/10 px-4 py-3">21</td>
                    <td className="border border-black/10 px-4 py-3">36</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Fit Guide */}
          <div className="bg-neutral-50 p-6">
            <h3 className="text-sm tracking-[0.2em] mb-4">FIT GUIDE</h3>
            <div className="space-y-3 text-sm text-neutral-600">
              <p><strong>RELAXED FIT:</strong> Our garments are designed with a relaxed, comfortable fit. If you prefer a more fitted look, consider sizing down.</p>
              <p><strong>BETWEEN SIZES:</strong> If you're between sizes, we recommend sizing up for a more comfortable fit.</p>
              <p><strong>FABRIC:</strong> All measurements are taken flat. Please allow 0.5-1 inch tolerance due to manual measurement.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
